/**
 * Geolocation Service
 * 
 * Provides IP-based country detection with fallback to Vietnam
 */

interface GeolocationResponse {
  country_name: string;
  country_code: string;
}

export class GeolocationService {
  private static readonly DEFAULT_COUNTRY = 'Vietnam';
  private static readonly DEFAULT_COUNTRY_CODE = 'VN';
  private static readonly CACHE_KEY = 'user_geolocation';
  private static readonly CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Get user's country from IP address
   */
  static async detectCountry(): Promise<{ country: string; countryCode: string }> {
    // Check cache first
    const cached = this.getCachedLocation();
    if (cached) {
      return cached;
    }

    try {
      // Try multiple free IP geolocation services
      const result = await this.tryGeolocationServices();
      
      // Cache the result
      this.cacheLocation(result);
      
      return result;
    } catch (error) {
      console.warn('Failed to detect country from IP, using default:', error);
      return {
        country: this.DEFAULT_COUNTRY,
        countryCode: this.DEFAULT_COUNTRY_CODE
      };
    }
  }

  /**
   * Try multiple geolocation services as fallbacks
   */
  private static async tryGeolocationServices(): Promise<{ country: string; countryCode: string }> {
    const services = [
      () => this.tryIpApi(),
      () => this.tryIpInfo(),
      () => this.tryIpGeolocation()
    ];

    for (const service of services) {
      try {
        const result = await service();
        if (result) {
          return result;
        }
      } catch (error) {
        console.warn('Geolocation service failed:', error);
        continue;
      }
    }

    // All services failed, return default
    return {
      country: this.DEFAULT_COUNTRY,
      countryCode: this.DEFAULT_COUNTRY_CODE
    };
  }

  /**
   * Try ipapi.co service
   */
  private static async tryIpApi(): Promise<{ country: string; countryCode: string } | null> {
    try {
      const response = await fetch('https://ipapi.co/json/', {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('API request failed');
      
      const data = await response.json();
      
      if (data.country_name && data.country_code) {
        return {
          country: data.country_name,
          countryCode: data.country_code.toUpperCase()
        };
      }
      
      return null;
    } catch (error) {
      console.warn('ipapi.co failed:', error);
      return null;
    }
  }

  /**
   * Try ipinfo.io service
   */
  private static async tryIpInfo(): Promise<{ country: string; countryCode: string } | null> {
    try {
      const response = await fetch('https://ipinfo.io/json', {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('API request failed');
      
      const data = await response.json();
      
      if (data.country) {
        // ipinfo.io only provides country code, we need to map it to full name
        const countryName = this.getCountryNameFromCode(data.country);
        return {
          country: countryName,
          countryCode: data.country.toUpperCase()
        };
      }
      
      return null;
    } catch (error) {
      console.warn('ipinfo.io failed:', error);
      return null;
    }
  }

  /**
   * Try ipgeolocation.io service
   */
  private static async tryIpGeolocation(): Promise<{ country: string; countryCode: string } | null> {
    try {
      const response = await fetch('https://api.ipgeolocation.io/ipgeo?apiKey=', {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('API request failed');
      
      const data = await response.json();
      
      if (data.country_name && data.country_code2) {
        return {
          country: data.country_name,
          countryCode: data.country_code2.toUpperCase()
        };
      }
      
      return null;
    } catch (error) {
      console.warn('ipgeolocation.io failed:', error);
      return null;
    }
  }

  /**
   * Get cached location if still valid
   */
  private static getCachedLocation(): { country: string; countryCode: string } | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();

      if (now - timestamp < this.CACHE_EXPIRY) {
        return data;
      }

      // Cache expired, remove it
      localStorage.removeItem(this.CACHE_KEY);
      return null;
    } catch (error) {
      console.warn('Failed to read cached location:', error);
      return null;
    }
  }

  /**
   * Cache location data
   */
  private static cacheLocation(location: { country: string; countryCode: string }): void {
    try {
      const cacheData = {
        data: location,
        timestamp: Date.now()
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to cache location:', error);
    }
  }

  /**
   * Map country code to country name (basic mapping)
   */
  private static getCountryNameFromCode(countryCode: string): string {
    const countryMap: Record<string, string> = {
      'VN': 'Vietnam',
      'US': 'United States',
      'GB': 'United Kingdom',
      'DE': 'Germany',
      'FR': 'France',
      'JP': 'Japan',
      'KR': 'South Korea',
      'CN': 'China',
      'IN': 'India',
      'AU': 'Australia',
      'CA': 'Canada',
      'BR': 'Brazil',
      'MX': 'Mexico',
      'IT': 'Italy',
      'ES': 'Spain',
      'RU': 'Russia',
      'TH': 'Thailand',
      'SG': 'Singapore',
      'MY': 'Malaysia',
      'ID': 'Indonesia',
      'PH': 'Philippines'
    };

    return countryMap[countryCode.toUpperCase()] || countryCode;
  }
}